"use client";

import { Suspense, useMemo, useState, useSyncExternalStore } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useProMode } from "@/hooks/use-pro-mode";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { useAppCore } from "@/hooks/use-app-core";
import { useAppItunes } from "@/hooks/use-app-itunes";
import { useAppSimilar } from "@/hooks/use-app-similar";
import { useAppCountryRankings } from "@/hooks/use-app-country-rankings";
import { usePageVisitLogger } from "@/hooks/use-page-visit-logger";
import { extractAppId, extractCountryCode, getAppField, type NormalizedAppData } from "@/lib/app-data-helpers";
import { AppDetailSkeleton } from "@/components/appstore/app-detail-skeleton";
import AppDetailProSkeleton from "@/components/app-detail-pro-skeleton";
import AppDetailMain from "@/components/app-detail-main";
import dynamic from "next/dynamic";
// Not in the first-load JS: the minimized view is pro mode only (never the
// server render), and the competitors column is lg:-only and fills from a
// client fetch, so neither has anything to hydrate on a phone. Both mount
// after hydration only, which is what keeps their chunks out of the HTML.
const AppDetailMainMinimized = dynamic(() => import("@/components/app-detail-main-minimized"), { ssr: false });
const SimilarAppsSidebar = dynamic(() => import("@/components/similar-apps-sidebar").then((m) => m.SimilarAppsSidebar), { ssr: false });
import { AppKeywordRanks } from "@/components/appstore/app-keyword-ranks";
import SearchTimelineSkeleton from "@/components/search/SearchTimelineSkeleton";
import MarketAnalysisSkeleton from "@/components/search/MarketAnalysisSkeleton";
import MarketConcentrationSkeleton from "@/components/search/MarketConcentrationSkeleton";
import CompetitiveBubbleSkeleton from "@/components/search/CompetitiveBubbleSkeleton";
import CompetitiveIntensitySkeleton from "@/components/search/CompetitiveIntensitySkeleton";
import AppAgeDistributionSkeleton from "@/components/search/AppAgeDistributionSkeleton";
import NewEntrantsSkeleton from "@/components/search/NewEntrantsSkeleton";
import {
  SearchTimeline,
  CompetitiveBubbleChart,
  MarketConcentration,
  MarketAnalysis,
  CompetitiveIntensity,
  AppAgeDistribution,
  NewEntrants,
} from "@/components/search/LazySearchComponents";
import { LazyMotion, domAnimation, m, AnimatePresence } from "framer-motion";

type AppDetailViewProps = {
  appId: number;
  initialData?: Partial<NormalizedAppData>;
  dataUpdatedAt?: number;
};

const contentVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

// Hydration-safe mounted flag: server renders `false`, client commits `true`
// in a single pass (no post-mount re-render flash).
const emptySubscribe = () => () => {};
function useMounted() {
  return useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false
  );
}

export function AppDetailView({ appId, initialData, dataUpdatedAt }: AppDetailViewProps) {
  const hydrated = useMounted();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isMobile = useIsMobile();
  const { isPro } = useProMode('normal');
  const { isTerminal } = useThemeConfig();

  // Hydration-safe terminal mode
  const mounted = useMounted();
  const showTerminal = mounted && isTerminal;
  
  // Three parallel slices replacing the old monolithic /api/app-detail call.
  // Core lands first (metadata + snapshot) and unblocks the page header.
  // iTunes enrichment + similar apps fill in afterward.
  const { data: coreData, isLoading: isLoadingCore, error: coreError } = useAppCore(appId, initialData, dataUpdatedAt);
  const { data: itunesData } = useAppItunes(appId);
  const { items: similarItems, isLoading: isLoadingSimilarFetch } = useAppSimilar(appId);

  // Pre-fetch country rankings immediately (don't wait for app-detail to complete)
  // This runs in parallel with the slices for faster perceived load time
  const { data: countryRankingsData, isLoading: isLoadingCountryRankings } = useAppCountryRankings(appId, true);

  // Merge slices into the same shape the children already consume. Core
  // arrives first; itunes/similar are spread on as they land.
  const data = useMemo(() => {
    if (!coreData && !itunesData) return null;
    return {
      ...(coreData || {}),
      ...(itunesData || {}),
      similar: similarItems,
    } as Partial<typeof coreData> & Record<string, any>;
  }, [coreData, itunesData, similarItems]);

  // Children gate inner skeletons on this. Core already carries description +
  // screenshots (from DB metadata or iTunes fallback persisted previously) so
  // we don't need to wait for the iTunes enrichment slice — that just adds
  // extras like genres / minimumOsVersion / releaseNotes.
  const isLoadingMetadata = isLoadingCore;
  const error = coreError;

  // Extract normalized IDs (memoized to prevent unnecessary re-renders)
  const aid = useMemo(() => extractAppId(data, appId), [data, appId]);
  const cid = useMemo(() => extractCountryCode(data), [data]);

  // Log page visit. Wait for core data so the country is the real one, not the
  // "us" fallback — otherwise the visit (and the app_viewed analytics event)
  // fires twice with two different countries.
  usePageVisitLogger({ appId: aid, countryCode: coreData ? cid : undefined });

  const isLoadingSimilar = isLoadingSimilarFetch && similarItems.length === 0;
  
  // Merge current app with similar apps for visualization
  const itemsWithCurrentApp = useMemo(() => {
    if (similarItems.length === 0 || !data) return [];
    
    const currentApp = {
      app_store_id: aid,
      name: getAppField<string>(data, 'name') || "Current App",
      icon_url: getAppField<string>(data, 'icon_url'),
      rating: getAppField<number>(data, 'average_rating'),
      rating_count: getAppField<number>(data, 'rating_count'),
      revenue_estimate: getAppField<number>(data, 'revenue_estimate'),
      downloads_estimate: getAppField<number>(data, 'downloads_estimate'),
      release_date: getAppField<string>(data, 'release_date'),
      updated_date: getAppField<string>(data, 'current_version_release_date'),
      price: getAppField<number>(data, 'price'),
      supported_languages: getAppField<string[]>(data, 'languages'),
      top_countries: (data as any)?.top_countries || null,
      publisher_name: getAppField<string>(data, 'developer_name'),
      isCurrentApp: true,
    };
    
    return [currentApp, ...similarItems];
  }, [similarItems, data, aid]);
  
  // Timeline items for charts
  const timelineItems = useMemo(() => (
    itemsWithCurrentApp.map((it: any) => ({
      icon_url: it.icon_url ?? null,
      release_date: it.release_date ?? null,
      revenue_estimate: typeof it.revenue_estimate === 'number' ? it.revenue_estimate : null,
      name: it.name,
    }))
  ), [itemsWithCurrentApp]);

  // Gate the page on the smallest possible payload (core slice only). This
  // is the metadata + current snapshot, ~200ms warm. Everything else
  // (description/screenshots from itunes, sidebar/charts from similar) fills
  // in progressively without blocking the first paint.
  return (
    <LazyMotion features={domAnimation}>
    {/* initial={false}: server-rendered content must not ship at opacity 0, or nothing paints until hydration. */}
    <AnimatePresence mode="wait" initial={false}>
      {!coreData ? (
        <m.div
          key="skeleton"
          variants={contentVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2 }}
        >
          {(!hydrated || !isPro) ? <AppDetailSkeleton /> : <AppDetailProSkeleton />}
        </m.div>
      ) : (
        <m.div
          key="content"
          variants={contentVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.3 }}
          id="app-detail"
          className={`text-foreground mx-auto ${showTerminal ? 'font-mono' : ''}`}
          style={showTerminal ? {
            backgroundColor: 'rgb(0, 0, 0)',
            minHeight: '100vh',
          } : undefined}
        >
          <div className="">
            <div
              /* 100vh minus the 3rem sticky top bar: at h-screen the grid's last 48px sat below the fold, which is what cut the globe off. */
              className={`grid grid-cols-1 lg:grid-cols-3 lg:h-[calc(100vh-3rem)] lg:overflow-hidden ${
                showTerminal ? 'border border-[color:rgba(var(--ast-brand-green-rgb),0.5)]' : ''
              }`}
              style={showTerminal ? {
                boxShadow: '0 0 20px rgba(var(--ast-brand-green-rgb),0.22)',
              } : undefined}
            >
              {isPro ? (
            <>
              {/* Column 1: Main App Details */}
              <div
                className={`md:p-3 p-3 lg:h-[calc(100vh-3rem)] lg:overflow-y-auto pb-20 col-span-1 ${
                  showTerminal ? 'border-r border-[color:rgba(var(--ast-brand-green-rgb),0.3)] bg-[var(--ast-brand-night)]' : ''
                }`}
              >
                <AppDetailMainMinimized appId={appId} data={data as any} />
              </div>

              {/* Column 2: Competitors (top) + Country Rankings (bottom) */}
              <div
                className={`col-span-1 ${
                  showTerminal
                    ? 'border-r border-[color:rgba(var(--ast-brand-green-rgb),0.3)] bg-[var(--ast-brand-night)]'
                    : 'border-r border-sidebar-border'
                }`}
              >
                {showTerminal && (
                  <div
                    className="px-3 py-1.5 border-b border-[color:rgba(var(--ast-brand-green-rgb),0.5)] text-[var(--ast-brand-green)] text-[10px] uppercase tracking-wider font-mono"
                    style={{ textShadow: '0 0 10px rgba(var(--ast-brand-green-rgb),0.5)' }}
                  >
                    COMPETITORS & RANKINGS
                  </div>
                )}
                <SimilarAppsSidebar
                  key={`split-${aid}-${cid}`}
                  appId={aid}
                  country={cid}
                  mode="competitors"
                  locked
                  overrideItems={itemsWithCurrentApp}
                  splitView={true}
                  prefetchedCountryRankings={countryRankingsData}
                  prefetchedCountryRankingsLoading={isLoadingCountryRankings}
                />
              </div>

              {/* Column 3: Market Intelligence & Competitive Analysis */}
              <div
                className={`lg:h-[calc(100vh-3rem)] lg:overflow-y-auto pb-20 col-span-1 ${
                  showTerminal ? 'bg-[var(--ast-brand-night)]' : ''
                }`}
              >
                {showTerminal && (
                  <div
                    className="px-3 py-1.5 border-b border-[color:rgba(var(--ast-brand-green-rgb),0.5)] text-[var(--ast-brand-green)] text-[10px] uppercase tracking-wider font-mono"
                    style={{ textShadow: '0 0 10px rgba(var(--ast-brand-green-rgb),0.5)' }}
                  >
                    MARKET INTELLIGENCE
                  </div>
                )}
                <div className="space-y-2 p-3">
                  {isLoadingSimilar ? (
                    <>
                      <SearchTimelineSkeleton rounded={true} />
                      <MarketAnalysisSkeleton />
                      <MarketConcentrationSkeleton />
                      <CompetitiveIntensitySkeleton />
                      <AppAgeDistributionSkeleton />
                      <NewEntrantsSkeleton />
                      <CompetitiveBubbleSkeleton />
                    </>
                  ) : (
                    <>
                      {timelineItems.length > 0 && (
                        <Suspense fallback={<SearchTimelineSkeleton rounded={true} />}>
                          <SearchTimeline items={timelineItems as any} rounded={true} appId={aid} country={cid} />
                        </Suspense>
                      )}

                      {itemsWithCurrentApp.length > 0 && (
                        <>
                          <Suspense fallback={<MarketAnalysisSkeleton />}>
                            <MarketAnalysis items={itemsWithCurrentApp as any} appId={aid} country={cid} />
                          </Suspense>
                          <Suspense fallback={<MarketConcentrationSkeleton />}>
                            <MarketConcentration items={itemsWithCurrentApp as any} appId={aid} country={cid} />
                          </Suspense>
                          <Suspense fallback={<CompetitiveIntensitySkeleton />}>
                            <CompetitiveIntensity items={itemsWithCurrentApp as any} appId={aid} country={cid} />
                          </Suspense>
                          <Suspense fallback={<AppAgeDistributionSkeleton />}>
                            <AppAgeDistribution items={itemsWithCurrentApp as any} appId={aid} country={cid} />
                          </Suspense>
                          <Suspense fallback={<NewEntrantsSkeleton />}>
                            <NewEntrants items={itemsWithCurrentApp as any} appId={aid} country={cid} />
                          </Suspense>
                          <Suspense fallback={<CompetitiveBubbleSkeleton />}>
                            <CompetitiveBubbleChart items={itemsWithCurrentApp as any} appId={aid} country={cid} />
                          </Suspense>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                className={`px-4 pt-6 sm:px-8 sm:pt-10 xl:px-12 lg:h-[calc(100vh-3rem)] lg:overflow-y-auto pb-24 col-span-2 ${
                  showTerminal ? 'border-r border-[color:rgba(var(--ast-brand-green-rgb),0.3)] bg-[var(--ast-brand-night)]' : ''
                }`}
              >
                {showTerminal && (
                  <div
                    className="mb-2 pb-1 border-b border-[color:rgba(var(--ast-brand-green-rgb),0.5)] text-[var(--ast-brand-green)] text-[10px] uppercase tracking-wider font-mono"
                    style={{ textShadow: '0 0 10px rgba(var(--ast-brand-green-rgb),0.5)' }}
                  >
                    APP DETAILS
                  </div>
                )}
                <AppDetailMain
                  appId={appId}
                  data={data as any}
                  isLoadingMetadata={isLoadingMetadata}
                  showAdvanced={showAdvanced}
                  setShowAdvanced={setShowAdvanced}
                  error={error}
                />
              </div>
              <div
                className={`col-span-1 hidden lg:flex lg:flex-col lg:h-[calc(100vh-3rem)] lg:overflow-hidden ${
                  showTerminal ? 'bg-[var(--ast-brand-night)]' : ''
                }`}
              >
                {showTerminal && (
                  <div
                    className="px-3 py-1.5 border-b border-[color:rgba(var(--ast-brand-green-rgb),0.5)] text-[var(--ast-brand-green)] text-[10px] uppercase tracking-wider font-mono"
                    style={{ textShadow: '0 0 10px rgba(var(--ast-brand-green-rgb),0.5)' }}
                  >
                    COMPETITORS & RANKINGS
                  </div>
                )}
                {/* After hydration only: rendered during SSR, even an ssr:false
                    dynamic() puts its chunk in the first-load <script> tags. */}
                {hydrated && (
                  <SimilarAppsSidebar
                    appId={aid}
                    country={cid}
                    mode="competitors"
                    overrideItems={itemsWithCurrentApp}
                    splitView={true}
                    prefetchedCountryRankings={countryRankingsData}
                    prefetchedCountryRankingsLoading={isLoadingCountryRankings}
                    className="min-h-0 flex-1 lg:h-full"
                    /* Search positions at the top of the column's bottom half,
                       scrolling as one region with Country Rankings. It renders
                       nothing for an app with no collected positions, which is
                       nearly every app. Duplicated with the lg:hidden mount inside
                       AppDetailMain because this column is hidden below lg; the
                       two never render at the same width and share one query
                       cache. */
                    middleSection={<AppKeywordRanks appId={aid} variant="card" />}
                  />
                )}
              </div>
            </>
          )}
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
    </LazyMotion>
  );
}

export default AppDetailView;
